"use client"

import { type Ref } from "react"
import { Popover as PopoverPrimitive } from "@base-ui/react/popover"
import { cn } from "cn"

import IconCross from "@/components/icons/cross"
import { ButtonProps } from "./button"
import { IconButton } from "./icon-button"

export type PopoverHandle = PopoverPrimitive.Root.Props["handle"]

function Popover({ ...props }: PopoverPrimitive.Root.Props) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />
}

function PopoverTrigger({
  openOnHover = true,
  delay = 30,
  ...props
}: PopoverPrimitive.Trigger.Props) {
  return (
    <PopoverPrimitive.Trigger
      data-slot="popover-trigger"
      openOnHover={openOnHover}
      delay={delay}
      {...props}
    />
  )
}

export type PopoverContentProps = PopoverPrimitive.Popup.Props &
  Pick<
    PopoverPrimitive.Positioner.Props,
    | "align"
    | "alignOffset"
    | "side"
    | "sideOffset"
    | "anchor"
    | "collisionAvoidance"
    | "positionMethod"
    | "disableAnchorTracking"
  > & {
    container?: PopoverPrimitive.Portal.Props["container"]
    animate?: boolean
    size?: "auto" | "anchor" | "sm" | "md" | "lg"
    inverse?: boolean
    positionerClassName?: string
  }

function PopoverContent({
  ref,
  container,
  className,
  align = "center",
  alignOffset = 0,
  side = "bottom",
  sideOffset = 4,
  anchor,
  collisionAvoidance,
  positionMethod,
  disableAnchorTracking,
  animate = true,
  size = "auto",
  inverse = false,
  positionerClassName,
  ...props
}: PopoverContentProps & { ref?: Ref<HTMLDivElement> }) {
  return (
    <PopoverPrimitive.Portal container={container}>
      <PopoverPrimitive.Positioner
        data-slot="popover-positioner"
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        anchor={anchor}
        collisionAvoidance={collisionAvoidance}
        positionMethod={positionMethod}
        // keep a PopoverArrow clear of the popup's rounded-2xl corners
        arrowPadding={16}
        disableAnchorTracking={disableAnchorTracking}
        className={cn("isolate z-overlay", positionerClassName)}
      >
        <PopoverPrimitive.Popup
          ref={ref}
          data-slot="popover-content"
          className={cn(
            "z-overlay flex max-h-(--available-height) w-fit max-w-(--available-width) origin-(--transform-origin) flex-col overflow-x-hidden overflow-y-auto overscroll-contain rounded-2xl bg-surface-2 p-(--popover-pad) text-base leading-tight text-foreground outline-hidden duration-100 data-closed:fade-out-0 data-closed:zoom-out-95 data-open:fade-in-0 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            animate &&
              "data-closed:pointer-events-none data-closed:animate-out data-open:animate-in-inert",
            size === "anchor" && "w-(--anchor-width)",
            size === "sm" && "w-popover-sm",
            size === "md" && "w-popover-md",
            size === "lg" && "w-popover-lg",
            inverse ? "bg-foreground text-background" : "shadow-ring-xl",
            className
          )}
          data-inverse={inverse || undefined}
          {...props}
        />
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  )
}
PopoverContent.displayName = "PopoverContent"

function PopoverClose({
  className,
  variant = "ghost",
  ...props
}: Omit<PopoverPrimitive.Close.Props, "className"> & {
  variant?: ButtonProps["variant"]
  className?: string
}) {
  return (
    <PopoverPrimitive.Close
      data-slot="popover-close"
      render={
        <IconButton
          size="xs"
          icon={{
            icon: IconCross,
            className: "text-[1.25em]",
            strokeWidth: 3,
          }}
          variant={variant}
          className={className}
          label="Close"
        />
      }
      {...props}
    />
  )
}

/**
 * A pointer at the anchor, for a popover that calls something out. Colors follow `inverse`.
 * The box clips a rotated square that has the popup's background and ring color, so the
 * ring runs unbroken around the pointer.
 */
function PopoverArrow({ className, ...props }: PopoverPrimitive.Arrow.Props) {
  return (
    <PopoverPrimitive.Arrow
      data-slot="popover-arrow"
      className={cn(
        "block h-1.5 w-3 overflow-clip before:absolute before:bottom-0 before:left-1/2 before:size-[calc(6px*sqrt(2))] before:-translate-x-1/2 before:translate-y-1/2 before:rotate-45 before:border before:border-separator before:bg-surface-2 before:bg-clip-padding before:content-[''] in-data-inverse:before:border-foreground in-data-inverse:before:bg-foreground data-[side=bottom]:-top-1.5 data-[side=left]:-right-2.25 data-[side=left]:rotate-90 data-[side=right]:-left-2.25 data-[side=right]:-rotate-90 data-[side=top]:-bottom-1.5 data-[side=top]:rotate-180",
        className
      )}
      {...props}
    />
  )
}

const createPopoverHandle = PopoverPrimitive.createHandle

export {
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  PopoverClose,
  createPopoverHandle,
}
